import { supabase } from '../supabase';
import { SOURCE_BRANCH } from '../../utils/deployInfo';

// ─── The both-tap offer (E3b-2a step 2; migration 055) ──────────────────────
// Two profile holders connect only when both have tapped (Mark, 2026-10-21).
// The server referees: one offer row per host QR room. The scanner opens it
// after claiming the room; each phone taps and polls; the second tap forms it
// (a first meeting's room record is written by the server then, both seats at
// once). Leaving, 5 s of silence, 60 s after the first tap, or the host's code
// moving on ends it with nothing recorded. Each answer is the offer from this
// phone's side:
//   { state: 'open'|'waiting'|'formed'|'over', role: 'host'|'guest', reason,
//     peerTapped, hostId, guestId, meeting, existingRoom }
// 'waiting' = I've tapped, they haven't. reason (over): left · silence ·
// expired · closed · busy · failed (the room was someone else's, or the two
// already share one the host's lookup missed).
//
// One call at a time per poll; a call that hasn't answered in 2 s is let go
// (the proximity check-in's rule) — the next poll asks again.

export const OFFER_POLL_MS = 1000;
const GIVE_UP_MS = 2000;

const call = async (fn, args) => {
  const controller = new AbortController();
  const giveUp = setTimeout(() => controller.abort(), GIVE_UP_MS);
  try {
    const { data, error } = await supabase.rpc(fn, args).abortSignal(controller.signal);
    if (error) {
      console.warn(`[OFFER] ${fn} failed:`, error.message);
      return null;
    }
    return data;
  } catch (err) {
    console.warn(`[OFFER] ${fn} gave up:`, err?.message);
    return null;
  } finally {
    clearTimeout(giveUp);
  }
};

/** The scanner, once it holds the claim: open the offer on the host's room. */
export const openOffer = (room, me) =>
  call('offer_open', { p_room: room, p_me: me, p_source_branch: SOURCE_BRANCH });

/**
 * A tap. The host's carries the meeting verdict and, on a re-meet, the room
 * the two already share (the scanner adopts both).
 */
export const tapOffer = (room, me, { meeting = null, existingRoom = null } = {}) =>
  call('offer_tap', {
    p_room: room,
    p_me: me,
    p_meeting: meeting,
    p_existing_room: existingRoom,
  });

/** One poll: stamps "still here" and answers the offer's state. */
export const offerState = (room, me) => call('offer_state', { p_room: room, p_me: me });

/** Leave: an open offer ends with nothing recorded; a formed one stays formed. */
export const leaveOffer = (room, me) => call('offer_leave', { p_room: room, p_me: me });

/** Formed or over: nothing more will change. */
export const isSettled = (answer) => answer?.state === 'formed' || answer?.state === 'over';

/**
 * Poll the offer about every second until it settles. Each answer goes to
 * onAnswer (a failed poll is skipped: the server's 5 s silence rule, not one
 * missed answer, decides the other phone has gone).
 *
 * @returns {() => void} stop
 */
export const watchOffer = (room, me, onAnswer, { intervalMs = OFFER_POLL_MS } = {}) => {
  let live = true;
  let timer = null;
  const tick = async () => {
    const answer = await offerState(room, me);
    if (!live) return;
    if (answer) onAnswer(answer);
    if (isSettled(answer)) {
      live = false;
      return;
    }
    timer = setTimeout(tick, intervalMs);
  };
  tick();
  return () => {
    live = false;
    clearTimeout(timer);
  };
};
